import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSeo from './useSeo';
import { getSquadron, SQUADRONS } from '../data/squadrons';
import { SQUADRON_COLORS } from '../data/squadron-colors';
import { KNOWLEDGE, FALLBACK_PITCH } from '../data/squadron-knowledge';
import { trackJoinDiscord } from '../lib/analytics';

const SITE = 'https://jointtaskforceheavy.com';
const DISCORD = 'https://discord.gg/sPavY3ZZQH';

// Full-width airframe lithograph. The real images are still being produced, so
// until <slug>.webp exists the <img> 404s, onError fires, and we fall back to a
// styled placeholder band. Drop the file in public/pics/squadrons/litho/ later
// and it appears automatically — no code change.
const LithographBanner = ({ src, airframe, name }) => {
  const [failed, setFailed] = useState(false);
  return (
    // Transparent — the parent supplies the shared blue→accent gradient so the
    // hero and this banner read as one continuous fade with no seam.
    <div className="relative w-full aspect-[2/1] sm:aspect-[3/1] lg:aspect-[3.5/1] overflow-hidden">
      {!failed && (
        <img
          src={src}
          alt={`${name} ${airframe} lithograph`}
          className="absolute inset-0 w-full h-full object-contain p-3 sm:p-5"
          onError={() => setFailed(true)}
        />
      )}
      {failed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <span className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-[0.15em] text-white/85">
            {airframe}
          </span>
          <span className="mt-3 text-[0.65rem] md:text-xs uppercase tracking-[0.35em] text-hvy-accent/90">
            Lithograph coming soon
          </span>
        </div>
      )}
    </div>
  );
};

// Section heading, matching the narrative sections above it.
const SectionHeading = ({ children }) => (
  <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight uppercase text-hvy-900 mt-12 mb-3">
    {children}
    <span className="block mt-3 h-1 w-16 bg-hvy-accent rounded-full" />
  </h2>
);

const Fact = ({ label, children }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">{label}</dt>
    <dd className="mt-1.5 text-gray-800">{children}</dd>
  </div>
);

// Survey-backed squadron knowledge from squadron-knowledge.js.
//
// Every field is optional and an empty one renders nothing — no placeholders,
// no "TBD". A squadron whose lead hasn't returned the intake form simply gets a
// shorter page rather than one padded with invented content. See the fill policy
// and house-voice spec at the top of squadron-knowledge.js.
const KnowledgePanel = ({ knowledge, accent, shortName }) => {
  if (!knowledge) return null;

  const { role, missions, recurring, quals, activeMembers, culture } = knowledge;
  const { nickname, motto, vibe } = culture || {};

  // A lead who hasn't returned the form gets the shared task-force pitch rather
  // than an empty section. Every other field stays absent — we substitute the
  // one claim that is true of every squadron, and invent nothing else.
  const pitch = knowledge.pitch || FALLBACK_PITCH;

  const hasFacts =
    role || missions?.length || recurring?.length || quals?.length || activeMembers || nickname || motto;
  if (!hasFacts && !pitch && !vibe) return null;

  return (
    <>
      {hasFacts && (
        <section>
          <SectionHeading>At a glance</SectionHeading>
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 sm:p-8 shadow-sm border border-gray-200/50">
            <dl className="space-y-6 text-base leading-normal">
              {role && <Fact label="Primary role">{role}</Fact>}

              {missions?.length > 0 && (
                <Fact label="Missions flown">
                  <div className="flex flex-wrap gap-2">
                    {missions.map((m) => (
                      <span
                        key={m}
                        className="inline-block rounded-full border px-3 py-1 text-sm font-medium bg-white/70"
                        style={{ borderColor: accent, color: accent }}
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </Fact>
              )}

              {recurring?.length > 0 && (
                <Fact label="Recurring training">
                  <ul className="space-y-1.5">
                    {recurring.map((r) => (
                      <li key={r.name}>
                        {r.name}
                        {r.cadence && <span className="text-gray-600"> — {r.cadence}</span>}
                      </li>
                    ))}
                  </ul>
                </Fact>
              )}

              {quals?.length > 0 && (
                <Fact label="Qualifications">
                  <ul className="list-disc list-inside space-y-1.5 marker:text-hvy-accent">
                    {quals.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                </Fact>
              )}

              {activeMembers && <Fact label="Active members">{activeMembers}</Fact>}
              {nickname && <Fact label="Nickname">{nickname}</Fact>}
              {motto && <Fact label="Motto"><em>{motto}</em></Fact>}
            </dl>
          </div>
        </section>
      )}

      <section>
        <SectionHeading>Flying with the {shortName}</SectionHeading>
        {vibe && <p>{vibe}</p>}
        <p className={vibe ? 'mt-4' : undefined}>{pitch}</p>
      </section>
    </>
  );
};

const NotFound = () => {
  useSeo({
    title: 'Squadron not found | Joint Task Force Heavy',
    description: 'That squadron page could not be found. Explore the DCS squadrons of Joint Task Force Heavy.',
    canonical: `${SITE}/squadrons`,
  });
  return (
    <main className="w-full px-6 pt-32 pb-24 text-center bg-gray-50">
      <h1 className="font-heading text-3xl font-bold uppercase text-hvy-900">Squadron not found</h1>
      <p className="mt-4 text-gray-600">
        Head back to <Link to="/squadrons" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">our DCS squadrons</Link>.
      </p>
    </main>
  );
};

const SquadronPage = () => {
  const { slug } = useParams();
  const sq = getSquadron(slug);

  // useSeo must run unconditionally (hooks rule); feed it the squadron when found
  // and a not-found variant otherwise.
  useSeo(
    sq
      ? {
          title: `${sq.seoLabel}: ${sq.name} | Joint Task Force Heavy`,
          description: `${sq.intro} Join Joint Task Force Heavy on Discord to fly the ${sq.airframe}.`.slice(0, 300),
          canonical: `${SITE}/squadrons/${sq.slug}`,
        }
      : {
          title: 'Squadron not found | Joint Task Force Heavy',
          description: 'Explore the DCS squadrons of Joint Task Force Heavy.',
          canonical: `${SITE}/squadrons`,
        }
  );

  if (!sq) return <NotFound />;

  const accent = SQUADRON_COLORS[sq.slug] || '#3d5a80';
  const related = SQUADRONS.filter((s) => s.group === sq.group && s.slug !== sq.slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Squadrons', item: `${SITE}/squadrons` },
          { '@type': 'ListItem', position: 3, name: sq.name, item: `${SITE}/squadrons/${sq.slug}` },
        ],
      },
      {
        '@type': 'Organization',
        name: `${sq.name} (Joint Task Force Heavy)`,
        url: `${SITE}/squadrons/${sq.slug}`,
        description: sq.blurb,
        parentOrganization: { '@type': 'Organization', name: 'Joint Task Force Heavy', url: `${SITE}/` },
      },
    ],
  };

  return (
    <div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero + lithograph share ONE continuous top-to-bottom gradient: HVY blue
          at the very top fading into the patch's accent at the bottom of the
          litho band — no seam between the two. */}
      <div style={{ backgroundColor: '#0f1923', backgroundImage: `linear-gradient(180deg, #0f1923 0%, ${accent} 100%)` }}>
        {/* Hero — logo prominent */}
        <section className="relative pt-28 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <nav className="text-xs uppercase tracking-widest text-gray-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2 text-gray-600">/</span>
            <Link to="/squadrons" className="hover:text-white">Squadrons</Link>
          </nav>
          <img
            src={sq.logo}
            alt={`${sq.name} insignia`}
            className="mx-auto h-32 md:h-40 w-auto object-contain drop-shadow-2xl"
          />
          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.25em] text-hvy-accent">
            {sq.seoLabel}
          </p>
          <h1 className="mt-2 font-heading text-4xl md:text-5xl font-bold uppercase tracking-wider text-white">
            {sq.name}
          </h1>
          <p className="mt-3 text-lg text-gray-300">
            {sq.airframe} &bull; {sq.group}
          </p>
        </div>
      </section>

        {/* Full-width airframe lithograph (placeholder until art lands) */}
        <LithographBanner src={sq.litho} airframe={sq.airframe} name={sq.name} />
      </div>

      {/* Content */}
      <main className="w-full px-6 py-12 bg-gray-50 bg-[url('bg.webp')] bg-repeat bg-fixed bg-top">
        <article className="max-w-3xl mx-auto text-gray-700 text-lg leading-relaxed">
          <p className="text-xl text-gray-800">{sq.intro}</p>

          {sq.body.map((section) => (
            <section key={section.heading}>
              <SectionHeading>{section.heading}</SectionHeading>
              <p>{section.text}</p>
            </section>
          ))}

          <KnowledgePanel knowledge={KNOWLEDGE[sq.slug]} accent={accent} shortName={sq.shortName} />

          {/* Join CTA */}
          <div className="mt-14 bg-white/60 backdrop-blur-sm rounded-xl p-8 shadow-sm border border-gray-200/50 text-center">
            <h2 className="font-heading text-2xl font-bold uppercase text-hvy-900 mb-3">
              Fly with {sq.shortName}
            </h2>
            <p className="text-base text-gray-600 mb-6 max-w-xl mx-auto">
              Joint Task Force Heavy is an 18+ DCS World community that welcomes new pilots. Join our
              Discord to start your application and fly the {sq.airframe}.
            </p>
            <a
              href={DISCORD}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackJoinDiscord('squadron', { squadron: sq.slug })}
              className="inline-block bg-discord hover:bg-discord/90 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 uppercase tracking-wider"
            >
              Join Our Discord
            </a>
          </div>

          {/* Related squadrons in the same group */}
          {related.length > 0 && (
            <div className="mt-14">
              <h2 className="font-heading text-xl font-bold uppercase text-hvy-900 mb-5">
                More from {sq.group}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/squadrons/${r.slug}`}
                    className="flex items-center gap-4 bg-white/60 backdrop-blur-sm rounded-xl p-4 shadow-sm border border-gray-200/50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <img src={r.logo} alt={`${r.name} insignia`} className="h-14 w-14 object-contain flex-shrink-0" loading="lazy" />
                    <div>
                      <div className="text-base font-semibold text-gray-900">{r.name}</div>
                      <div className="text-sm text-hvy-500 font-medium">{r.airframe}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 pt-8 border-t border-gray-200 text-center text-base text-gray-600">
            <p>
              See all our <Link to="/squadrons" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">DCS squadrons</Link>,
              read our <Link to="/join-dcs-squadron" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">guide to joining a DCS squadron</Link>,
              or return to the <Link to="/" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">home page</Link>.
            </p>
          </div>
        </article>
      </main>
    </div>
  );
};

export default SquadronPage;
